import { useEffect, useRef, useState } from "react";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { employeeService } from "../../services/EmployeeService";
import { useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

export default function EmployeeListPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    const searchInputRef = useRef(null);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    const navigate = useNavigate();
    const toast = useRef(null);

    const loadEmployees = async () => {
        try {
            setLoading(true);
            const res = await employeeService.list();
            if (res.data.success) {
                setEmployees(res.data.data || []);
            }
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: "error",
                summary: "Hata",
                detail: "Çalışan listesi yüklenirken bir hata oluştu.",
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEmployees();
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 0);
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters["global"].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const balanceTemplate = (rowData) => {
        const balance = rowData.balance || 0;
        const isNegative = balance < 0;
        return (
            <span className={`font-bold ${isNegative ? "text-red-500" : "text-green-600"}`}>
                {balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
            </span>
        );
    };

    const handleDeleteEmployee = (row) => {
        confirmDialog({
            message: `${row.fullName} isimli çalışan silinecektir. Emin misiniz?`,
            header: "Çalışan Sil",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Evet",
            rejectLabel: "Hayır",
            acceptClassName: "p-button-danger",
            accept: async () => {
                try {
                    await employeeService.delete(row.id);
                    toast.current?.show({
                        severity: "success",
                        summary: "Başarılı",
                        detail: "Çalışan başarıyla silindi.",
                        life: 3000
                    });
                    loadEmployees();
                } catch (err) {
                    console.error(err);
                    toast.current?.show({
                        severity: "error",
                        summary: "Hata",
                        detail: "Çalışan silinirken bir hata oluştu.",
                        life: 3000
                    });
                }
            }
        });
    };

    const actionsTemplate = (row) => (
        <div className="flex gap-1 justify-content-center">
            <Button
                icon="pi pi-pencil"
                rounded
                text
                severity="info"
                tooltip="Düzenle"
                className="w-8 h-8"
                onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/employees/edit/${row.id}`);
                }}
            />
            <Button
                icon="pi pi-trash"
                rounded
                text
                severity="danger"
                tooltip="Sil"
                className="w-8 h-8"
                onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteEmployee(row);
                }}
            />
            <Button
                icon="pi pi-eye"
                rounded
                text
                severity="help"
                tooltip="Detay"
                className="w-8 h-8"
                onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/employees/detail/${row.id}`);
                }}
            />
        </div>
    );

    const header = (
        <div className="flex flex-column md:flex-row justify-content-between align-items-center gap-2 py-1">
            <IconField iconPosition="left" className="w-full md:w-48">
                <InputIcon className="pi pi-search text-sm"> </InputIcon>
                <InputText
                    ref={searchInputRef}
                    value={globalFilterValue}
                    onChange={onGlobalFilterChange}
                    placeholder="Ara..."
                    className="p-inputtext-sm border-round-lg w-full"
                />
            </IconField>
        </div>
    );

    return (
        <div className="bg-[#f8fafc] min-h-screen animate-fade-in">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />
            <ConfirmDialog />

            <div className="bg-white shadow-lg shadow-slate-200/50 border border-slate-200 rounded-2xl overflow-hidden">
                <div className="!bg-slate-300 border-b border-slate-200 px-4 py-4 md:px-6 md:py-5 flex flex-col md:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="!bg-indigo-600 p-4 rounded-xl shadow-md shadow-indigo-500/20">
                            <i className="pi pi-id-card text-white text-lg"></i>
                        </div>
                        <div>
                            <h1 className="text-xl md:text-2xl font-black text-slate-900 uppercase tracking-tight">Çalışan Listesi</h1>
                            <p className="text-slate-400 text-xs font-bold italic">Çalışan Yönetimi ve Takibi</p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Button
                            label="YENİ ÇALIŞAN EKLE"
                            icon="pi pi-plus"
                            className="!bg-indigo-600 border-none hover:!bg-indigo-700 px-5 py-2.5 rounded-xl font-black text-xs shadow-lg shadow-indigo-500/20 transition-all active:scale-95"
                            onClick={() => navigate("/employees/add")}
                        />
                    </div>
                </div>

                <div className="p-2 md:p-4">
                    <DataTable
                        value={employees}
                        paginator
                        rows={15}
                        rowsPerPageOptions={[15, 25, 50]}
                        loading={loading}
                        filters={filters}
                        globalFilterFields={["fullName", "phoneNumber", "position"]}
                        header={header}
                        stripedRows
                        rowHover
                        showGridlines
                        responsiveLayout="stack"
                        breakpoint="960px"
                        onRowDoubleClick={(e) => navigate(`/employees/detail/${e.data.id}`)}
                        className="p-datatable-sm"
                    >
                        <Column
                            field="fullName"
                            header="Ad Soyad"
                            sortable
                            className="font-bold text-900"
                            style={{ width: '30%' }}
                        />
                        <Column field="phoneNumber" header="Telefon" style={{ width: '20%' }} />
                        <Column field="position" header="Pozisyon" style={{ width: '20%' }} />
                        <Column
                            field="balance"
                            header="Bakiye"
                            sortable
                            body={balanceTemplate}
                            style={{ width: '15%' }}
                            align="right"
                        />
                        <Column
                            header="Durum"
                            style={{ width: '10%' }}
                            align="center"
                            body={(row) => (
                                <Tag
                                    value={row.isActive ? "AKTİF" : "PASİF"}
                                    severity={row.isActive ? "success" : "secondary"}
                                    className="rounded-lg font-black text-[10px] px-3 py-1 border-none"
                                />
                            )}
                        />
                        <Column body={actionsTemplate} header="İşlemler" style={{ width: '10%' }} align="center" />
                    </DataTable>
                </div>
            </div>
        </div>
    );
}
